import { Bell, CheckCheck, ShoppingBag, Wallet, Shield, AlertTriangle, Megaphone, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useNotifications, Notification } from "@/hooks/useNotifications";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow, isToday, isYesterday } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

function groupByDate(items: Notification[]) {
  const groups: { label: string; items: Notification[] }[] = [];
  const today: Notification[] = [];
  const yesterday: Notification[] = [];
  const earlier: Notification[] = [];

  for (const n of items) {
    const d = new Date(n.created_at);
    if (isToday(d)) today.push(n);
    else if (isYesterday(d)) yesterday.push(n);
    else earlier.push(n);
  }

  if (today.length) groups.push({ label: "Today", items: today });
  if (yesterday.length) groups.push({ label: "Yesterday", items: yesterday });
  if (earlier.length) groups.push({ label: "Earlier", items: earlier });
  return groups;
}

function getCategoryIcon(category: string) {
  switch (category) {
    case "p2p": return <ShoppingBag className="h-3.5 w-3.5" />;
    case "wallet": return <Wallet className="h-3.5 w-3.5" />;
    case "security": return <Shield className="h-3.5 w-3.5" />;
    case "dispute": return <AlertTriangle className="h-3.5 w-3.5" />;
    case "merchant": return <Settings className="h-3.5 w-3.5" />;
    default: return <Megaphone className="h-3.5 w-3.5" />;
  }
}

function getSeverityColor(severity: string) {
  switch (severity) {
    case "success": return "bg-green-500";
    case "warning": return "bg-yellow-500";
    case "urgent": return "bg-destructive";
    default: return "bg-primary";
  }
}

export default function NotificationBell() {
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead, markAsSeen } = useNotifications();
  const navigate = useNavigate();
  const recent = notifications.slice(0, 8);
  const groups = groupByDate(recent);
  const prevUnreadRef = useRef(unreadCount);
  const initializedRef = useRef(false);
  const [ringing, setRinging] = useState(false);

  // Detect new notifications arriving and trigger ring animation
  useEffect(() => {
    if (loading) return;
    if (!initializedRef.current) {
      initializedRef.current = true;
      prevUnreadRef.current = unreadCount;
      return;
    }
    if (unreadCount > prevUnreadRef.current) {
      setRinging(true);
      const newestUnread = notifications.find((notification) => !notification.read);
      if (newestUnread) {
        toast(newestUnread.title, {
          description: newestUnread.message,
          action: newestUnread.link ? {
            label: "View",
            onClick: () => {
              markAsRead(newestUnread.id);
              navigate(newestUnread.link as string);
            },
          } : undefined,
        });
      }
      const timeout = setTimeout(() => setRinging(false), 1500);
      prevUnreadRef.current = unreadCount;
      return () => clearTimeout(timeout);
    }
    prevUnreadRef.current = unreadCount;
  }, [loading, markAsRead, navigate, notifications, unreadCount]);


  const handleOpenChange = (open: boolean) => {
    if (open) {
      // Mark visible unseen notifications as seen
      const unseenIds = recent.filter((n) => !n.is_seen).map((n) => n.id);
      if (unseenIds.length) markAsSeen(unseenIds);
    }
  };

  return (
    <DropdownMenu modal={false} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className={`h-5 w-5 transition-transform ${ringing ? "animate-bell-ring" : ""}`} />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground animate-in zoom-in-50 duration-200">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" className="w-80 z-50 p-0">
        <div className="flex items-center justify-between px-3 py-2">
          <p className="text-sm font-semibold">Notifications</p>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto py-1 px-2 text-xs"
              onClick={(e) => {
                e.preventDefault();
                markAllAsRead();
              }}
            >
              <CheckCheck className="h-3 w-3 mr-1" /> Mark all read
            </Button>
          )}
        </div>
        <DropdownMenuSeparator className="my-0" />
        <ScrollArea className="max-h-[320px]">
          {groups.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">
              No notifications yet
            </div>
          ) : (
            <div className="p-1">
              {groups.map((group) => (
                <div key={group.label}>
                  <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                    {group.label}
                  </p>
                  {group.items.map((n) => (
                    <DropdownMenuItem
                      key={n.id}
                      className="flex items-start gap-3 px-3 py-2.5 cursor-pointer"
                      onClick={() => {
                        if (!n.read) markAsRead(n.id);
                        if (n.link) navigate(n.link);
                      }}
                    >
                      <div className="mt-1 shrink-0 text-muted-foreground">
                        {getCategoryIcon(n.category)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          {!n.read && (
                            <div className={`h-2 w-2 rounded-full shrink-0 ${getSeverityColor(n.severity)}`} />
                          )}
                          <p className={`text-sm leading-tight ${n.read ? "text-muted-foreground" : "font-medium"}`}>
                            {n.title}
                          </p>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>
                        <p className="text-[10px] text-muted-foreground/70 mt-1">
                          {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                        </p>
                      </div>
                    </DropdownMenuItem>
                  ))}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
        <DropdownMenuSeparator className="my-0" />
        <DropdownMenuItem
          className="justify-center text-sm text-primary font-medium cursor-pointer m-1"
          onClick={() => navigate("/app/notifications")}
        >
          View all notifications
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
